import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Toast } from '@ds/components/feedback/Toast';

export interface ToastInput {
  tone?: 'neutral' | 'positive' | 'negative' | 'warning' | 'info';
  title: string;
  description?: string;
  /** ms até auto-dispensar. 0 desativa. Default 6000. */
  duration?: number;
  /** `data-testid` do toast; default `toast-{tone}`. Use um id da spec quando a tela exigir um específico. */
  testId?: string;
}
interface ToastItem extends ToastInput {
  id: number;
}

const ToastContext = createContext<((toast: ToastInput) => void) | null>(null);

/** Reutilizável: host de toasts (pilha fixa, aria-live via Toast do DS que já usa role="status"). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: ToastInput) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, duration: 6000, ...toast }]);
      const duration = toast.duration ?? 6000;
      if (duration > 0) setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="ca-toast-region"
        data-testid="toast-region"
        aria-live="polite"
        style={{
          position: 'fixed',
          bottom: 'var(--space-6)',
          right: 'var(--space-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          zIndex: 1000,
          maxWidth: 360,
        }}
      >
        {toasts.map((t) => (
          <Toast
            key={t.id}
            tone={t.tone}
            title={t.title}
            description={t.description}
            onClose={() => dismiss(t.id)}
            data-testid={t.testId ?? `toast-${t.tone ?? 'neutral'}`}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast precisa estar dentro de <ToastProvider>');
  return ctx;
}
